import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Shield, FileText, Lock, Eye, AlertCircle, CheckCircle,
  XCircle, Clock, Mail, RefreshCw, Ban, Gift, Package,
  ChevronDown, ChevronUp, Star, Zap, Award
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const SectionCard = ({ icon, title, color, children, index }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden"
  >
    <div className={`h-1.5 ${color}`} />
    <div className="p-7">
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-white shadow-md ${color}`}>
          {icon}
        </div>
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  </motion.div>
);

const PolicyPoint = ({ title, items, accent = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
  };
  return (
    <div className="mb-5">
      <h3 className={`inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border mb-3 ${colors[accent]}`}>
        {title}
      </h3>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
            <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function PricingPolicyPage() {
  const [openFaq, setOpenFaq] = useState(null);

  const compensationTable = [
    {
      num: 1,
      situation: 'Late or Missed Delivery',
      situationDesc: 'If your assignment or journal is not delivered on time or by the agreed deadline date.',
      commitment: '2 Free Deliveries',
      commitmentDesc: 'Your next 2 assignment or journal deliveries will be completely free of charge as compensation.',
      situationIcon: '⏰',
      commitmentIcon: '🎁',
      commitmentColor: 'text-orange-500',
    },
    {
      num: 2,
      situation: 'Damaged Journal by Our Company',
      situationDesc: 'If your journal is damaged due to any fault on our end during handling or delivery.',
      commitment: 'Full Replacement + Free Delivery',
      commitmentDesc: 'We will purchase a new journal, complete it fully, and deliver it to you at absolutely no cost.',
      situationIcon: '📔',
      commitmentIcon: '✅',
      commitmentColor: 'text-emerald-600',
    },
  ];

  const faqs = [
    { q: 'How do I raise a refund request?', a: 'You can raise a refund request through the website dashboard or by emailing support@agoinnovations.in within 3–7 days of delivery.' },
    { q: 'When will my refund be processed?', a: 'After verification, refunds are approved or rejected within 2–3 business days, and credited within 5–10 business days to your original payment method.' },
    { q: 'Can I cancel after my order is shipped?', a: 'Cancellation is not allowed after shipment except for valid reasons. You can cancel before shipment for a full refund.' },
    { q: 'Is my personal data safe?', a: 'Yes. Your data is stored securely and never shared with unauthorized third parties. You can request data deletion anytime.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">

      {/* ── HERO ── */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 pt-28 pb-20 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-1/4 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl" />
        </div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-4xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold mb-6">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            Policies & Pricing
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-4 leading-tight">
            Pricing List &{' '}
            <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
              Policies
            </span>
          </h1>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            Everything you need to know about our refund, cancellation, terms, privacy policies and how we protect you.
          </p>
        </motion.div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-16 space-y-10">

        {/* ── REFUND & COMPENSATION TABLE ── */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden"
        >
          {/* Table Header */}
          <div className="text-center px-6 pt-10 pb-6">
            <span className="text-orange-500 font-bold text-sm tracking-widest uppercase">Customer Protection</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mt-2 mb-2">
              Refund &amp; Compensation Policy
            </h2>
            <p className="text-gray-500 text-base">We stand behind our service. Here's how we protect you.</p>
          </div>

          <div className="px-6 pb-8">
            {/* Table */}
            <div className="rounded-2xl overflow-hidden border border-gray-200">
              {/* Table Head */}
              <div className="bg-blue-600 grid grid-cols-12 px-5 py-4 text-white text-sm font-bold tracking-wider uppercase">
                <div className="col-span-1">#</div>
                <div className="col-span-5">Situation</div>
                <div className="col-span-6">Our Commitment</div>
              </div>

              {/* Table Rows */}
              {compensationTable.map((row, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-12 px-5 py-6 gap-4 items-start ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-t border-gray-100`}
                >
                  {/* Number */}
                  <div className="col-span-1">
                    <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-black text-sm">
                      {row.num}
                    </span>
                  </div>

                  {/* Situation */}
                  <div className="col-span-5">
                    <div className="flex items-start gap-2">
                      <span className="text-xl">{row.situationIcon}</span>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{row.situation}</p>
                        <p className="text-gray-500 text-xs mt-1 leading-relaxed">{row.situationDesc}</p>
                      </div>
                    </div>
                  </div>

                  {/* Commitment */}
                  <div className="col-span-6">
                    <div className="flex items-start gap-2">
                      <span className="text-xl">{row.commitmentIcon}</span>
                      <div>
                        <p className={`font-bold text-sm ${row.commitmentColor}`}>{row.commitment}</p>
                        <p className="text-gray-500 text-xs mt-1 leading-relaxed">{row.commitmentDesc}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-center text-gray-400 text-xs mt-5">
              Our policies reflect our commitment to quality and customer satisfaction. For any concerns, please reach out to our support team.
            </p>
          </div>
        </motion.div>

        {/* ── OTS SERVICE PRICING ── */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden"
        >
          <div className="text-center px-6 pt-10 pb-6">
            <span className="text-orange-500 font-bold text-sm tracking-widest uppercase">One Time Service</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mt-2 mb-2">OTS Service Pricing</h2>
            <p className="text-gray-500 text-base">Complete pricing for Assignment &amp; Journal services with delivery charges</p>
          </div>

          <div className="px-6 pb-10 space-y-8">

            {/* ASSIGNMENT PRICING */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-black text-sm">📝</div>
                <h3 className="text-lg font-black text-blue-700">Assignment Pricing (per page)</h3>
              </div>
              <div className="rounded-2xl overflow-hidden border border-gray-200 mb-4">
                <div className="bg-blue-600 grid grid-cols-3 px-5 py-3 text-white text-sm font-bold uppercase tracking-wider">
                  <div>Service Type</div><div className="text-center">Rate / Page</div><div className="text-center">Deadline</div>
                </div>
                {[
                  { type: 'Normal Service', rate: '₹7', deadline: '4+ days', bg: 'bg-white' },
                  { type: 'Urgent — 3 days', rate: '₹9', deadline: '3 days', bg: 'bg-blue-50' },
                  { type: 'Urgent — 2 days', rate: '₹10', deadline: '2 days', bg: 'bg-white' },
                  { type: 'Urgent — 1 day', rate: '₹12', deadline: '1 day', bg: 'bg-blue-50' },
                ].map((row, i) => (
                  <div key={i} className={`grid grid-cols-3 px-5 py-4 border-t border-gray-100 ${row.bg}`}>
                    <p className="font-semibold text-gray-800 text-sm">{row.type}</p>
                    <p className="text-center font-black text-blue-700 text-lg">{row.rate}</p>
                    <p className="text-center text-gray-500 text-sm">{row.deadline}</p>
                  </div>
                ))}
              </div>

              {/* Assignment Delivery Charges */}
              <p className="text-sm font-bold text-gray-700 mb-2">📦 Assignment Delivery Charges</p>
              <div className="rounded-2xl overflow-hidden border border-gray-200">
                <div className="bg-indigo-600 grid grid-cols-12 px-5 py-3 text-white text-sm font-bold uppercase tracking-wider">
                  <div className="col-span-6">Destination Area</div>
                  <div className="col-span-3 text-center">Normal Papers</div>
                  <div className="col-span-3 text-center">College Papers (×2)</div>
                </div>
                {[
                  ['Kalyan', 'Free', 'Free'],
                  ['Shahad, Ambivli', '₹15', '₹30'],
                  ['Thakurli, Dombivli, Kopar', '₹15', '₹30'],
                  ['Vithalwadi, Ulhasnagar, Ambarnath', '₹20', '₹40'],
                  ['Titwala, Khadavli, Vasind', '₹25', '₹50'],
                  ['Diva–Kanjurmarg, Badlapur, Vangani', '₹30', '₹60'],
                  ['Asangaon–Khardi, Vikhroli–Dadar, Neral–Kelavli, Airoli, Rabale', '₹40', '₹80'],
                  ['Parel–CSMT, Matunga Road–Vile Parle, Ghansoli–Turbhe, Tilaknagar–Mankhurd, Kings Circle', '₹50', '₹100'],
                  ['Vasai–Panvel Branch, Dolavli, Lowjee, Khopoli', '₹50–60', '₹100–120'],
                  ['Prabhadevi–Mumbai Central, Andheri–Mira Road, Sanpada–CBD Belapur, Targhar–Gavhan', '₹60', '₹120'],
                  ['Kharghar, Mansarovar, Khandeshwar, Umbermali, Kasara', '₹70', '₹140'],
                  ['Grant Road–Churchgate, Bhayandar–Virar, Shematikhar–Uran', '₹80', '₹160'],
                  ['Vaitarna–Kelve Road', '₹90', '₹180'],
                  ['Palghar, Umroli, Boisar', '₹100', '₹200'],
                  ['Vangaon, Dahanu Road', '₹110', '₹220'],
                ].map(([area, normal, college], i) => (
                  <div key={i} className={`grid grid-cols-12 px-5 py-3 border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <p className="col-span-6 text-gray-700 text-sm">{area}</p>
                    <p className="col-span-3 text-center font-bold text-indigo-700 text-sm">{normal}</p>
                    <p className="col-span-3 text-center font-bold text-purple-700 text-sm">{college}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* JOURNAL PRICING */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center text-white font-black text-sm">📔</div>
                <h3 className="text-lg font-black text-emerald-700">Journal Pricing (per page)</h3>
              </div>
              <div className="rounded-2xl overflow-hidden border border-gray-200 mb-4">
                <div className="bg-emerald-600 grid grid-cols-3 px-5 py-3 text-white text-sm font-bold uppercase tracking-wider">
                  <div>Service Type</div><div className="text-center">Rate / Page</div><div className="text-center">Deadline</div>
                </div>
                {[
                  { type: 'Normal Service', rate: '₹5', deadline: '4+ days', bg: 'bg-white' },
                  { type: 'Urgent — 3 days', rate: '₹7', deadline: '3 days', bg: 'bg-emerald-50' },
                  { type: 'Urgent — 2 days', rate: '₹8', deadline: '2 days', bg: 'bg-white' },
                  { type: 'Urgent — 1 day', rate: '₹9', deadline: '1 day', bg: 'bg-emerald-50' },
                ].map((row, i) => (
                  <div key={i} className={`grid grid-cols-3 px-5 py-4 border-t border-gray-100 ${row.bg}`}>
                    <p className="font-semibold text-gray-800 text-sm">{row.type}</p>
                    <p className="text-center font-black text-emerald-700 text-lg">{row.rate}</p>
                    <p className="text-center text-gray-500 text-sm">{row.deadline}</p>
                  </div>
                ))}
              </div>

              {/* Journal Delivery Charges (all ×2) */}
              <p className="text-sm font-bold text-gray-700 mb-2">📦 Journal Delivery Charges <span className="text-purple-600 font-normal text-xs">(all charges ×2 — special handling)</span></p>
              <div className="rounded-2xl overflow-hidden border border-gray-200">
                <div className="bg-emerald-600 grid grid-cols-12 px-5 py-3 text-white text-sm font-bold uppercase tracking-wider">
                  <div className="col-span-9">Destination Area</div>
                  <div className="col-span-3 text-center">Delivery Charge</div>
                </div>
                {[
                  ['Kalyan', 'Free'],
                  ['Shahad, Ambivli, Thakurli, Dombivli, Kopar', '₹30'],
                  ['Vithalwadi, Ulhasnagar, Ambarnath', '₹40'],
                  ['Titwala, Khadavli, Vasind', '₹50'],
                  ['Diva–Kanjurmarg, Badlapur, Vangani', '₹60'],
                  ['Asangaon–Khardi, Vikhroli–Dadar, Neral–Kelavli, Airoli, Rabale', '₹80'],
                  ['Parel–CSMT, Matunga Road–Vile Parle, Ghansoli–Turbhe, Tilaknagar–Mankhurd, Kings Circle, Vasai–Panvel Branch', '₹100'],
                  ['Dolavli, Lowjee, Khopoli, Prabhadevi–Mumbai Central, Andheri–Mira Road, Sanpada–CBD Belapur, Targhar–Gavhan', '₹120'],
                  ['Kharghar, Mansarovar, Khandeshwar, Umbermali, Kasara', '₹140'],
                  ['Grant Road–Churchgate, Bhayandar–Virar, Shematikhar–Uran', '₹160'],
                  ['Vaitarna–Kelve Road', '₹180'],
                  ['Palghar, Umroli, Boisar', '₹200'],
                  ['Vangaon, Dahanu Road', '₹220'],
                ].map(([area, charge], i) => (
                  <div key={i} className={`grid grid-cols-12 px-5 py-3 border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <p className="col-span-9 text-gray-700 text-sm">{area}</p>
                    <p className="col-span-3 text-center font-black text-emerald-700 text-sm">{charge}</p>
                  </div>
                ))}
              </div>
              <p className="text-center text-gray-400 text-xs mt-3">Journal delivery charges are 2× the standard rate due to special handling requirements.</p>
            </div>

            {/* Metro & Monorail note */}
            <div className="p-4 bg-violet-50 border border-violet-200 rounded-2xl text-sm text-violet-700">
              <p className="font-bold mb-1">🚇 Metro &amp; Monorail Delivery Charges</p>
              <p className="text-xs leading-relaxed">Metro and Monorail delivery charges vary by station (₹60–₹170 for Metro · ₹60–₹120 for Monorail). Select your station in the OTS portal to see the exact charge. Journal deliveries are charged at ×2 for all Metro &amp; Monorail stations.</p>
            </div>

          </div>
        </motion.div>

        {/* ── OTS SERVICE RULES & AGREEMENT ── */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden"
        >
          <div className="h-1.5 bg-gradient-to-r from-red-500 to-orange-500" />
          <div className="p-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-2xl bg-red-500 flex items-center justify-center text-white shadow-md">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">OTS Service — Rules & Agreement</h2>
                <p className="text-xs text-gray-500 mt-0.5">These rules apply when you submit a One Time Service (OTS) request</p>
              </div>
            </div>

            {/* Alert banner */}
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl mb-6">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 font-semibold">
                By submitting an OTS request and completing payment, you confirm that you have read, understood, and agreed to all the rules listed below.
              </p>
            </div>

            {/* Rules */}
            <div className="space-y-4">

              {/* Rule 1 */}
              <div className="flex gap-4 p-5 bg-red-50 border border-red-200 rounded-2xl">
                <div className="w-9 h-9 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-black text-sm shadow">
                  1
                </div>
                <div>
                  <p className="font-bold text-red-700 text-sm mb-1">No Refund Policy</p>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Once you make the payment, the amount will <span className="font-bold text-red-600">NOT be refunded</span> under any circumstances. Please ensure all your details, page count, and deadline are correct before proceeding to payment.
                  </p>
                </div>
              </div>

              {/* Rule 2 */}
              <div className="flex gap-4 p-5 bg-orange-50 border border-orange-200 rounded-2xl">
                <div className="w-9 h-9 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-black text-sm shadow">
                  2
                </div>
                <div>
                  <p className="font-bold text-orange-700 text-sm mb-1">Personal Responsibility</p>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    If anyone from your home — parents, relatives, or guardians — complains about the use of this service, AGO Innovations will <span className="font-bold text-orange-600">not take any responsibility</span>. You are completing and submitting your assignments with your own responsibility and full awareness.
                  </p>
                </div>
              </div>

              {/* Rule 3 — Voluntary Use */}
              <div className="flex gap-4 p-5 bg-blue-50 border border-blue-200 rounded-2xl">
                <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-black text-sm shadow">
                  3
                </div>
                <div>
                  <p className="font-bold text-blue-700 text-sm mb-1">Voluntary Use & Full Accountability</p>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    By using this service, you acknowledge that you are doing so <span className="font-bold text-blue-600">voluntarily</span> and take full responsibility for your decision. AGO Innovations acts only as a service provider and is not liable for any academic, institutional, or personal consequences arising from your use of this service.
                  </p>
                </div>
              </div>

            </div>

            {/* Agreement reminder */}
            <div className="flex items-start gap-3 mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-700 leading-relaxed">
                <span className="font-bold">Agreement:</span> Submitting the OTS form after payment is considered acceptance of all the above rules and regulations. A confirmation checkbox is shown before final submission in the OTS Service Portal.
              </p>
            </div>
          </div>
        </motion.div>

        {/* ── REFUND & CANCELLATION ── */}
        <SectionCard icon={<RefreshCw className="w-5 h-5" />} title="Refund & Cancellation Policy" color="bg-blue-600" index={1}>
          <PolicyPoint accent="blue" title="Refund Eligibility" items={[
            'Product received is damaged or defective',
            'Wrong product delivered',
            'Order not delivered within the promised time',
            'Refund requests must be raised within 3–7 days of delivery',
          ]} />
          <PolicyPoint accent="green" title="Refund Process" items={[
            'Request via website dashboard or customer support email/contact form',
            'Approved or rejected within 2–3 business days after verification',
            'Approved refunds processed within 5–10 business days to original payment method',
          ]} />
          <PolicyPoint accent="orange" title="Cancellation Policy" items={[
            'Before shipment → Full refund',
            'After shipment → Cancellation not allowed (except valid reasons)',
            'No charges if cancelled before processing',
            'A small processing fee (5–10%) may apply after confirmation',
          ]} />
          <PolicyPoint accent="red" title="Non-Refundable Cases" items={[
            'Change of mind after delivery',
            'Incorrect address provided by user',
            'Misuse or damage by the customer',
          ]} />
          <div className="flex items-center gap-3 mt-4 p-4 bg-blue-50 rounded-2xl border border-blue-100">
            <Mail className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-800">Contact for Refunds</p>
              <p className="text-sm text-blue-600">help.agoinnovations@gmail.com · Response time: 24–48 hours</p>
            </div>
          </div>
        </SectionCard>

        {/* ── TERMS & CONDITIONS ── */}
        <SectionCard icon={<FileText className="w-5 h-5" />} title="Terms & Conditions" color="bg-indigo-600" index={2}>
          <PolicyPoint accent="purple" title="Acceptance of Terms" items={[
            'By using the website, users agree to comply with all terms and policies.',
          ]} />
          <PolicyPoint accent="blue" title="User Responsibilities" items={[
            'Provide accurate information during registration/order',
            'Avoid fraudulent activities and misuse of platform services',
            'Maintain confidentiality of login credentials',
          ]} />
          <PolicyPoint accent="green" title="Company Responsibilities" items={[
            'Provide services/products as described',
            'Ensure secure transactions',
            'Offer support for user queries and complaints',
          ]} />
          <PolicyPoint accent="red" title="Usage Restrictions & Account Suspension" items={[
            'Users must not use the platform for illegal purposes',
            'Attempting to hack, disrupt, or damage the system is prohibited',
            'Copying or misusing website content is not allowed',
            'Company reserves the right to suspend accounts involved in suspicious activity',
            'Users violating policies may be permanently blocked',
          ]} />
        </SectionCard>

        {/* ── LIABILITY DISCLAIMER ── */}
        <SectionCard icon={<AlertCircle className="w-5 h-5" />} title="Liability Disclaimer" color="bg-orange-500" index={3}>
          <PolicyPoint accent="orange" title="Limitation of Liability" items={[
            'The company is not liable for delays caused by external factors (courier, natural events)',
            'Not liable for technical issues beyond control',
            'Maximum liability is limited to the order value paid',
          ]} />
          <PolicyPoint accent="blue" title="Third-Party Services" items={[
            'The platform may use third-party services (payment gateways, logistics)',
            'The company is not responsible for their independent failures',
          ]} />
        </SectionCard>

        {/* ── PRIVACY & DATA ── */}
        <SectionCard icon={<Lock className="w-5 h-5" />} title="Privacy & Data Usage Policy" color="bg-emerald-600" index={4}>
          <PolicyPoint accent="green" title="Data Collection & Usage" items={[
            'We collect: Name, email, phone number, address and transaction details',
            'Used for order processing, customer support, and service improvement',
          ]} />
          <PolicyPoint accent="blue" title="Data Protection & Cookies" items={[
            'User data is stored securely and not shared with unauthorized parties',
            'Website may use cookies to enhance user experience and track performance',
          ]} />
          <PolicyPoint accent="purple" title="User Rights" items={[
            'Users can request data deletion at any time',
            'Users can update their personal information through the dashboard',
          ]} />
        </SectionCard>

        {/* ── FAQ ── */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-3xl shadow-lg border border-gray-100 p-7"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-2xl bg-yellow-400 flex items-center justify-center shadow-md">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left bg-gray-50 hover:bg-blue-50 transition-colors duration-200"
                >
                  <span className="font-semibold text-gray-800 text-sm">{faq.q}</span>
                  {openFaq === i
                    ? <ChevronUp className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-5 py-4 text-gray-600 text-sm leading-relaxed border-t border-gray-100 bg-white"
                  >
                    {faq.a}
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── CONTACT CTA ── */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-gradient-to-br from-blue-800 to-indigo-900 rounded-3xl p-6 sm:p-10 text-center text-white"
        >
          <Award className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-2xl font-black mb-2">Have Questions?</h2>
          <p className="text-blue-200 mb-6 max-w-xl mx-auto text-sm">
            Our support team is here to help. Reach out anytime and we'll get back to you within 24–48 hours.
          </p>
          <div className="flex justify-center">
            <a
              href="mailto:help.agoinnovations@gmail.com"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold rounded-xl transition-all duration-200 shadow-lg text-sm sm:text-base max-w-full break-all text-center"
            >
              <Mail className="w-4 h-4 flex-shrink-0" />
              <span className="break-all">help.agoinnovations@gmail.com</span>
            </a>
          </div>
        </motion.div>

      </div>

      {/* Bottom border */}
      <div className="h-1 bg-gradient-to-r from-yellow-400 via-blue-500 to-yellow-400" />
    </div>
  );
}
